// AlertEditor.jsx

import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Select, Input, Button, Collapse, Table, message, Space, AutoComplete, Switch } from "antd";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import TextCrafter from './TextCrafter';
import ClauseEditor from "./ClauseEditor";
import AlertTestingResultsTable from "./AlertTestingResultsTable";
import { testClause, testAlert, copyAlert } from "../functions/alertClauseService";
import { fetchScopeAffirmations } from "../functions/alertScopeService";


const { Panel } = Collapse;

const AlertEditor = ({ visible, onClose, alertData, onSave, clauses, dashboardStructure, refreshAlerts }) => {

  const [form] = Form.useForm();
  const [isTextCrafterVisible, setIsTextCrafterVisible] = useState(false);
  const [craftedText, setCraftedText] = useState(alertData?.advice_txt || "");
  const [isClauseEditorVisible, setIsClauseEditorVisible] = useState(false);
  const [editingClause, setEditingClause] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [isTestResultsModalVisible, setIsTestResultsModalVisible] = useState(false); // Modal visibility for AlertTestingResultsTable
  const [currentTestResults, setCurrentTestResults] = useState([]); // Store results for display in modal
  const [alertTestCount, setAlertTestCount] = useState(null);
  // const [alertTestCount, setAlertTestCount] = useState(0); // Number of alerts from the test
  const [alertTestResults, setAlertTestResults] = useState([]); // Store results for Alert Test
  const [isTestingAlert, setIsTestingAlert] = useState(false); // Spinner state for Alert Test
  const [scopeAffirmations, setScopeAffirmations] = useState([]); // Store scope/alert-type affirmations

// console.log("alertData in AlertEditor ", alertData)
// console.log("dasboardstructure in alerteditor", dashboardStructure )

const handleCopyAlert = () => {
  Modal.confirm({
    title: "Are you sure?",
    content: "Do you want to create a copy of this alert?",
    okText: "Yes",
    cancelText: "No",
    onOk: async () => {
      try {
        const response = await copyAlert(alertData);
        message.success("Alert copied successfully!");
        if (refreshAlerts) {
          refreshAlerts(response); // Refresh alerts with the newly created alert
        }
      } catch (error) {
        console.error("Error copying alert:", error);
        message.error("Failed to copy alert.");
      }
    },
  });
}; 

// Function to handle Alert Test and save results
const handleTestAlert = async () => {
  setIsTestingAlert(true); // Start spinner
  setAlertTestCount(null); // Clear results while testing
  try {
    const result = await testAlert(alertData.ID);
    setAlertTestCount(result.length);
    setAlertTestResults(result);
    message.success('Alert tested successfully!');
  } catch (error) {
    console.error("Error testing alert:", error);
    message.error('Error testing alert.');
  } finally {
    setIsTestingAlert(false); // Stop spinner
  }
};

// Open AlertTestingResultsTable with alert test results
const handleOpenAlertTestResultsModal = () => {
  setCurrentTestResults(alertTestResults);
  setIsTestResultsModalVisible(true);
};
//   console.log('clauses in AlertEditor ', clauses)
//   console.log("dashboardStructure in AlertEditor ", dashboardStructure)
// console.log("alertData in ALertEditor ", alertData)
// console.log("clauses in AlertEditor ", clauses)
  
  const [availableClauses, setAvailableClauses] = useState([]); // Assuming this gets populated from props or an API
  useEffect(() => {
    setAvailableClauses(
      clauses.map((clause) => ({
        value: clause.ID.toString(),  // Convert ID to string for consistency
        label: `ID: ${clause.ID}, ${clause.clause_txt || "No text available"}`, // Default text if `clause_txt` is undefined
      }))
    );
}, [clauses]);

const [localClauses, setLocalClauses] = useState(
    alertData && Array.isArray(alertData.clauses)
      ? alertData.clauses
      : String(alertData?.clauses || "")
          .split(",")
          .map((id) => parseInt(id, 10))  // Ensure IDs are numbers
          .filter((id) => !isNaN(id))  // Filter out any invalid IDs
  );

  // ✨ NEW: State for affirmative clauses (DAS-1169)
  const [localAffirmativeClauses, setLocalAffirmativeClauses] = useState(
    alertData && Array.isArray(alertData.affirmative_clauses)
      ? alertData.affirmative_clauses
      : String(alertData?.affirmative_clauses || "")
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id))
  );
  

  // Handler for adding an existing required clause
const handleAddExistingClause = (clauseId) => {
    const clauseToAdd = clauses.find((clause) => clause.ID === parseInt(clauseId, 10));
    const clauseIdInt = parseInt(clauseId, 10);

    // ✅ Validation: Check if clause is already used as affirmative clause
    if (localAffirmativeClauses.includes(clauseIdInt)) {
      message.error(`Clause ${clauseIdInt} is already used as an Affirmative Clause. A clause cannot be both required and affirmative.`);
      return;
    }

    if (clauseToAdd && !localClauses.includes(clauseToAdd.ID)) {
        const updatedClauses = [...localClauses, clauseToAdd.ID];
        
        // Update both localClauses and associatedClauses to trigger re-render
        setLocalClauses(updatedClauses);
        message.success(`Required clause ${clauseIdInt} added successfully`);
    }
  };

  // ✨ NEW: Handler for adding an existing affirmative clause (DAS-1169)
  const handleAddExistingAffirmativeClause = (clauseId) => {
    const clauseToAdd = clauses.find((clause) => clause.ID === parseInt(clauseId, 10));
    const clauseIdInt = parseInt(clauseId, 10);

    // ✅ Validation: Check if clause is already used as required clause
    if (localClauses.includes(clauseIdInt)) {
      message.error(`Clause ${clauseIdInt} is already used as a Required Clause. A clause cannot be both required and affirmative.`);
      return;
    }

    if (clauseToAdd && !localAffirmativeClauses.includes(clauseToAdd.ID)) {
        const updatedClauses = [...localAffirmativeClauses, clauseToAdd.ID];
        setLocalAffirmativeClauses(updatedClauses);
        message.success(`Affirmative clause ${clauseIdInt} added successfully`);
    }
  };

  // ✨ NEW: Handler for removing affirmative clause (DAS-1169)
  // ✨ ENHANCED: Added confirmation dialog (DAS-1208)
  const handleRemoveAffirmativeClause = (clauseId) => {
    // Find the clause details for the confirmation message
    const clauseToRemove = clauses.find((clause) => clause.ID === clauseId);
    const clauseText = clauseToRemove ? clauseToRemove.clause_txt : `Clause ID ${clauseId}`;
    
    Modal.confirm({
      title: 'Remove Affirmative Clause',
      content: (
        <div>
          <p>Are you sure you want to remove this affirmative clause?</p>
          <div style={{ 
            marginTop: '12px', 
            padding: '8px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <strong>Clause ID:</strong> {clauseId}<br/>
            <strong>Clause Text:</strong> {clauseText}
          </div>
        </div>
      ),
      okText: 'Yes, Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        // Remove the clause from local state
        setLocalAffirmativeClauses(localAffirmativeClauses.filter((id) => id !== clauseId));
        message.success(`Affirmative clause ${clauseId} removed. Don't forget to save your changes!`);
      },
      onCancel() {
        // User cancelled, do nothing
        console.log('Clause removal cancelled by user');
      },
    });
  };
  
  
  const handleAddNewClause = () => {
    setEditingClause({}); // Open ClauseEditor with a blank clause object
    setIsClauseEditorVisible(true);
  };
  
  const handleNewClauseSaved = (newClause) => {
    if (newClause) {
      // Add new clause ID to localClauses
      setLocalClauses((prevClauses) => {
        const updatedClauses = [...new Set([...prevClauses, newClause.ID])]; // Ensure unique IDs
        form.setFieldsValue({ clauses: updatedClauses.join(",") });
        return updatedClauses;
      });
  
      // Add new clause to availableClauses if not already present
      setAvailableClauses((prevAvailableClauses) => {
        const exists = prevAvailableClauses.some(
          (clause) => parseInt(clause.value, 10) === newClause.ID
        );
        if (!exists) {
          return [
            ...prevAvailableClauses,
            {
              value: newClause.ID.toString(),
              label: `ID: ${newClause.ID}, ${newClause.clause_txt || "No text available"}`,
            },
          ];
        }
        return prevAvailableClauses;
      });
    }
  };
  



useEffect(() => {
  if (alertData) {
    form.setFieldsValue({
      ...alertData,
      status: alertData.status === 1, // Convert status to boolean for the Switch
    });
    setCraftedText(alertData.advice_txt || "");
    setLocalClauses(
      alertData.clauses
        ? alertData.clauses.split(",").map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
        : []
    );
    // ✨ NEW: Handle affirmative clauses (DAS-1169)
    setLocalAffirmativeClauses(
      alertData.affirmative_clauses
        ? alertData.affirmative_clauses.split(",").map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
        : []
    );
  } else {
    form.resetFields();
    setCraftedText("");
    setLocalClauses([]);
    setLocalAffirmativeClauses([]); // ✨ NEW: Reset affirmative clauses
  }
}, [alertData, form]);



  // Sync `craftedText` with `alertData.advice_txt` only if `advice_txt` changes
  useEffect(() => {
      setCraftedText(alertData?.advice_txt || "");
    }, [alertData?.advice_txt]);

  // Fetch scope/alert-type affirmations
  useEffect(() => {
    const loadScopeAffirmations = async () => {
      try {
        const affirmations = await fetchScopeAffirmations();
        setScopeAffirmations(affirmations);
      } catch (error) {
        console.error('Error loading scope affirmations:', error);
      }
    };
    if (visible) {
      loadScopeAffirmations();
    }
  }, [visible]);

  useEffect(() => {
        if (alertData && alertData.clauses) {
            setLocalClauses(
                alertData.clauses.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
            );
        } else {
            setLocalClauses([]); // Reset if there are no clauses
        }
    }, [alertData]);

   


    const associatedClauses = useMemo(() => {
      const allClauses = clauses.filter((clause) => localClauses.includes(clause.ID));
      const uniqueClauses = Array.from(new Map(allClauses.map((clause) => [clause.ID, clause])).values());
      return uniqueClauses;
    }, [clauses, localClauses]);

    // ✨ NEW: Associated affirmative clauses (DAS-1169)
    const associatedAffirmativeClauses = useMemo(() => {
      const allClauses = clauses.filter((clause) => localAffirmativeClauses.includes(clause.ID));
      const uniqueClauses = Array.from(new Map(allClauses.map((clause) => [clause.ID, clause])).values());
      return uniqueClauses;
    }, [clauses, localAffirmativeClauses]);
    
    
    


      const handleSave = () => {
        form.validateFields().then((values) => {
          const updatedData = { 
            ...alertData, 
            ...values, 
            advice_txt: craftedText, 
            clauses: localClauses.join(","), // Ensure clauses are saved as a comma-separated string
            affirmative_clauses: localAffirmativeClauses.join(","), // ✨ NEW: Include affirmative clauses (DAS-1169)
            status: values.status ? 1 : 0, // Map Switch state to database values (1 or 0)
          };
      
          if (!updatedData.ID) {
            // For new alert creation
            onSave(updatedData, "create");
          } else {
            // For updating an existing alert
            onSave(updatedData, "update");
          }
      
          // Refresh alerts after saving
          if (refreshAlerts) {
            refreshAlerts(updatedData); // Pass the updated alert data to refresh
          }
      
          message.success("Alert saved successfully!");
          // onClose(); // Optionally close the modal
        }).catch((error) => {
          console.error("Validation failed:", error);
        });
      };
      
      
      
  

  const openTextCrafter = () => {
    setIsTextCrafterVisible(true);
  };

  const handleTextCrafterSave = (newText) => {
    setCraftedText(newText);
    setIsTextCrafterVisible(false);
    form.setFieldsValue({ advice_txt: newText });
  };

  // Updated handleTestClause to store count and array separately
const handleTestClause = async (clause) => {
  try {
      const result = await testClause(clause); // Assume `result` is an array of test results
      const resultCount = result.length;

      setTestResults((prevResults) => ({
          ...prevResults,
          [clause.ID]: { count: `${resultCount} True`, results: result } // Store count and results array
      }));

      message.info(`${resultCount} True`); // Show a message with count of True results

  } catch (error) {
      console.error("Error in handleTestClause:", error);
  }
};

// Updated handleOpenTestResultsModal to set currentTestResults correctly
const handleOpenTestResultsModal = (clauseId) => {
  const clauseData = testResults[clauseId];
  setCurrentTestResults(clauseData?.results || []); // Set the results array for modal
  setIsTestResultsModalVisible(true);
};

  const handleEditClause = (clause) => {
    setEditingClause(clause);
    setIsClauseEditorVisible(true);
  };

  const handleDisassociateClause = (clauseId) => {
    // Filter out the disassociated clause ID from alertData.clauses
    const updatedClauses = localClauses.filter((id) => id !== clauseId);
  
    // Update `localClauses` to keep internal state in sync
    setLocalClauses(updatedClauses);
};


  // Define columns for the clauses table
  // In AlertEditor, update clauseColumns
const clauseColumns = [
  { title: "Clause ID", dataIndex: "ID", key: "ID" },
  { title: "Field", dataIndex: "clause_field", key: "clause_field" },
  { title: "Operator", dataIndex: "clause_operator", key: "clause_operator" },
  { 
    title: "Clause Text", 
    dataIndex: "clause_txt", 
    key: "clause_txt",
    render: (text, clause) => (
      <>
        <div>{text}</div>
        {testResults[clause.ID]?.count && (
          <div>
            <a 
              onClick={() => handleOpenTestResultsModal(clause.ID)} 
              style={{ color: "blue", cursor: "pointer" }}
            >
              {testResults[clause.ID].count} {/* Display link like "6 True" */}
            </a>
          </div>
        )}
      </>
    )
  },
  { title: "Threshold Field", dataIndex: "threshold_field", key: "threshold_field" },
  { title: "Source Table", dataIndex: "clause_field_sourcetable", key: "clause_field_sourcetable" },
  {
    title: "Action",
    key: "action",
    render: (_, clause) => (
      <>
        <Button type="link" onClick={() => handleTestClause(clause)}>Test</Button>
        <Button type="link" onClick={() => handleEditClause(clause)}>Edit</Button>
        <Button type="link" danger onClick={() => handleDisassociateClause(clause.ID)}>Disassociate</Button>
      </>
    ),
  },
];

const handleOk = () => {
  if (refreshAlerts) {
    refreshAlerts(); // Trigger refresh in MarketAlerts.jsx
  }
  onClose(); // Close the AlertEditor modal
};

  return (
    <Modal
      title={`Edit Alert ${alertData?.ID ? `(${alertData.ID})` : ""}`}
      visible={visible}
      onCancel={onClose}
      footer={[
        <Space key="footerActions">
          <Button key="alertTest" onClick={handleTestAlert} loading={isTestingAlert}>
            {isTestingAlert ? "Testing..." : "Alert Test"}
          </Button>
          {alertTestCount === 0 && !isTestingAlert && (
            <span style={{ color: "gray" }}>No alerts</span>
          )}
          {alertTestCount > 0 && !isTestingAlert && (
            <a
              onClick={handleOpenAlertTestResultsModal}
              style={{ color: "blue", cursor: "pointer" }}
            >
              {alertTestCount} Alerts
            </a>
          )}
          {alertTestCount === null && !isTestingAlert && (
            <span style={{ color: "gray" }}>&nbsp;</span> // Empty space when no test is run yet
          )}
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button key="copy" type="primary" onClick={handleCopyAlert}>
            Copy Alert
          </Button>
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>
          <Button key="ok" type="primary" onClick={handleOk}>
            OK
          </Button>
        </Space>
      ]}
      
      
      width={2000}
    >
      <Form form={form} layout="vertical">
        {/* Form Items for Alert Fields */}
        <Form.Item
  label="Status"
  name="status"
  valuePropName="checked" // Maps the Switch's checked state to true/false
  rules={[{ required: true, message: "Status is required" }]}
>
  <Switch
    checkedChildren="Active"
    unCheckedChildren="Inactive"
    defaultChecked={alertData?.status === 1}
    onChange={(checked) => form.setFieldsValue({ status: checked ? 1 : 0 })} // Set 1 for Active, 0 for Inactive
  />
</Form.Item>

        <Form.Item
          label="Scope"
          name="scope"
          rules={[{ required: true, message: "Scope is required" }]}
        >
          <Select placeholder="Select scope">
            <Select.Option value="Portfolio">Portfolio</Select.Option>
            <Select.Option value="Favorites">Favorites</Select.Option>
            <Select.Option value="General">General</Select.Option>
            <Select.Option value="Unknown">Unknown</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Alert Type"
          name="alert_type"
          rules={[{ required: true, message: "Alert type is required" }]}
        >
          <Input placeholder="Enter alert type" />
        </Form.Item>

        <Form.Item
          label="Alert Subtype"
          name="alert_subtype"
          rules={[{ required: true, message: "Alert subtype is required" }]}
        >
          <Input placeholder="Enter alert subtype" />
        </Form.Item>

        {/* DAS-3289: the register signal this alert IS. Set it and the urgency score gives the
            alert no extra Alpha component (its rule already is the proven signal: no double count). */}
        <Form.Item
          label="Register signal this alert is (urgency Alpha gated off)"
          name="register_signal_key"
          tooltip="Leave empty for most alerts. Fill in the Alpha register signal key when the alert's own rule is that signal, e.g. good_fund_buyers, cheap_pe_after_stress, cheap_pe_sector, small_cheap_pe, zacks_value_a."
        >
          <Input data-testid="register-signal-key" placeholder="empty, or e.g. cheap_pe_after_stress" allowClear />
        </Form.Item>

        <Form.Item
          label="Advice Text"
          name="advice_txt"
          rules={[{ required: true, message: "Advice text is required" }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Enter advice text"
            value={craftedText}
            onClick={openTextCrafter}
            readOnly
          />
        </Form.Item>

        <Form.Item
  label="Required Clauses (Knockout Criteria)"
  name="clauses"
  rules={[
    {
      required: true,
      message: "At least one required clause is needed",
      validator: (_, value) =>
        localClauses.length > 0
          ? Promise.resolve()
          : Promise.reject(new Error("At least one required clause is required")),
    },
  ]}
>
  <Input 
    placeholder="Enter required clause IDs separated by commas"
    value={localClauses.join(",")}  // Show clauses as a comma-separated string
    onChange={(e) => {
      const newClauses = e.target.value.split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id));
      
      // ✅ Validation: Check for conflicts with affirmative clauses
      const conflicts = newClauses.filter(clauseId => localAffirmativeClauses.includes(clauseId));
      if (conflicts.length > 0) {
        message.error(`Clause(s) ${conflicts.join(', ')} are already used as Affirmative Clauses. Please remove them from affirmative clauses first.`);
        return;
      }
      
      // ✅ Validation: Check for conflicts with scope/alert-type affirmation clauses
      const scopeAlertTypeAffirmations = scopeAffirmations.find(
        affirmation => affirmation.Scope === alertData?.scope && affirmation.alert_type === alertData?.alert_type
      );
      if (scopeAlertTypeAffirmations?.affirmative_clauses) {
        const scopeClauseIds = scopeAlertTypeAffirmations.affirmative_clauses.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
        const scopeConflicts = newClauses.filter(clauseId => scopeClauseIds.includes(clauseId));
        if (scopeConflicts.length > 0) {
          message.error(`Clause(s) ${scopeConflicts.join(', ')} are already used in Scope-Alert Type Affirmation Clauses. Please remove them from scope affirmations first.`);
          return;
        }
      }
      
      setLocalClauses(newClauses);
    }}  // Convert the input back to an array of integers with validation
  />
</Form.Item>


        {/* Collapsible section for required clauses */}
     
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 16 }}>
          <Panel 
            header={
              <Space>
                <span style={{ color: '#d4380d', fontWeight: 'bold' }}>🚫 Required Clauses (Knockout Criteria)</span>
                <AutoComplete
                        style={{ width: 400 }}
                        options={availableClauses.filter(clause => {
                          const clauseId = parseInt(clause.value, 10);
                          // Filter out already used clauses
                          if (localClauses.includes(clauseId) || localAffirmativeClauses.includes(clauseId)) {
                            return false;
                          }
                          // Filter out scope/alert-type affirmation clauses
                          const scopeAlertTypeAffirmations = scopeAffirmations.find(
                            affirmation => affirmation.Scope === alertData?.scope && affirmation.alert_type === alertData?.alert_type
                          );
                          if (scopeAlertTypeAffirmations?.affirmative_clauses) {
                            const scopeClauseIds = scopeAlertTypeAffirmations.affirmative_clauses.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
                            if (scopeClauseIds.includes(clauseId)) {
                              return false;
                            }
                          }
                          return true;
                        })}
                        onSelect={handleAddExistingClause}
                        placeholder="Add Existing Required Clause"
                        filterOption={(inputValue, option) =>
                            option.label.toLowerCase().includes(inputValue.toLowerCase())
                        }
                    />
                <Button onClick={handleAddNewClause}>Add a NEW Clause</Button>
              </Space>
            }
            key="1"
          >
            <div style={{ marginBottom: '8px', color: '#666', fontSize: '12px' }}>
              These clauses must ALL be true for the alert to trigger. If any required clause fails, the alert will NOT be generated.
            </div>
            <Table
              columns={clauseColumns}
              dataSource={associatedClauses}
              pagination={false}
              rowKey="ID"
              bordered
              size="small"
            />
          </Panel>
        </Collapse>

        {/* ✨ NEW: Affirmative Clauses Section (DAS-1169) */}
        <Form.Item
          label="Affirmative Clauses (Supporting Evidence)"
          name="affirmative_clauses"
          style={{ marginTop: 16 }}
        >
          <Input 
            placeholder="Enter affirmative clause IDs separated by commas (optional)"
            value={localAffirmativeClauses.join(",")}
            onChange={(e) => {
              const newClauses = e.target.value.split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id));
              
              // ✅ Validation: Check for conflicts with required clauses
              const conflicts = newClauses.filter(clauseId => localClauses.includes(clauseId));
              if (conflicts.length > 0) {
                message.error(`Clause(s) ${conflicts.join(', ')} are already used as Required Clauses. Please remove them from required clauses first.`);
                return;
              }
              
              // ✅ Validation: Check for conflicts with scope/alert-type affirmation clauses
              const scopeAlertTypeAffirmations = scopeAffirmations.find(
                affirmation => affirmation.Scope === alertData?.scope && affirmation.alert_type === alertData?.alert_type
              );
              if (scopeAlertTypeAffirmations?.affirmative_clauses) {
                const scopeClauseIds = scopeAlertTypeAffirmations.affirmative_clauses.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
                const scopeConflicts = newClauses.filter(clauseId => scopeClauseIds.includes(clauseId));
                if (scopeConflicts.length > 0) {
                  message.error(`Clause(s) ${scopeConflicts.join(', ')} are already used in Scope-Alert Type Affirmation Clauses. Please remove them from scope affirmations first.`);
                  return;
                }
              }
              
              setLocalAffirmativeClauses(newClauses);
            }}
          />
        </Form.Item>

        {/* Collapsible section for affirmative clauses */}
        <Collapse style={{ marginTop: 16 }}>
          <Panel 
            header={
              <Space>
                <span style={{ color: '#389e0d', fontWeight: 'bold' }}>✅ Associated Affirmative Clauses (Supporting Evidence)</span>
                <AutoComplete
                  style={{ width: 400 }}
                  options={availableClauses.filter(clause => {
                    const clauseId = parseInt(clause.value, 10);
                    // Filter out clauses already used as affirmative OR required
                    if (localAffirmativeClauses.includes(clauseId) || localClauses.includes(clauseId)) {
                      return false;
                    }
                    // Filter out scope/alert-type affirmation clauses
                    const scopeAlertTypeAffirmations = scopeAffirmations.find(
                      affirmation => affirmation.Scope === alertData?.scope && affirmation.alert_type === alertData?.alert_type
                    );
                    if (scopeAlertTypeAffirmations?.affirmative_clauses) {
                      const scopeClauseIds = scopeAlertTypeAffirmations.affirmative_clauses.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
                      if (scopeClauseIds.includes(clauseId)) {
                        return false;
                      }
                    }
                    return true;
                  })}
                  onSelect={handleAddExistingAffirmativeClause}
                  placeholder="Add Existing Affirmative Clause"
                  filterOption={(inputValue, option) =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Space>
            }
            key="2"
          >
            <div style={{ marginBottom: '8px', color: '#666', fontSize: '12px' }}>
              These clauses provide supporting evidence to strengthen the alert value. They are evaluated ONLY if all required clauses pass. 
              Affirmative clauses can be true or false - they will NOT prevent alert generation.
            </div>
            
            {/* Scope/Alert-Type Affirmation Clauses Display */}
            {(() => {
              const scopeAlertTypeAffirmations = scopeAffirmations.find(
                affirmation => affirmation.Scope === alertData?.scope && affirmation.alert_type === alertData?.alert_type
              );
              const scopeAlertTypeClauses = scopeAlertTypeAffirmations?.affirmative_clauses || '';
              
              if (scopeAlertTypeClauses) {
                return (
                  <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f8ff', border: '1px solid #91d5ff', borderRadius: '6px' }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                      📋 Scope-Alert Type Affirmation Clauses (Read-Only)
                    </div>
                    <div style={{ color: '#666', fontSize: '12px', marginBottom: '8px' }}>
                      These clauses are automatically applied to all alerts with scope "{alertData?.scope}" and alert type "{alertData?.alert_type}". 
                      They cannot be edited here but are included in the alert evaluation.
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                      Scope-Alert type AFF: {scopeAlertTypeClauses}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <Table
              columns={[
                ...clauseColumns.slice(0, -1), // All columns except Actions
                {
                  title: "Actions",
                  key: "actions",
                  width: 180,
                  render: (text, record) => (
                    <Space>
                      <Button size="small" onClick={() => handleTestClause(record)}>
                        Test
                      </Button>
                      <Button size="small" icon={<EditOutlined />} onClick={() => handleEditClause(record)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveAffirmativeClause(record.ID)}
                      >
                        Remove
                      </Button>
                    </Space>
                  ),
                }
              ]}
              dataSource={associatedAffirmativeClauses}
              pagination={false}
              rowKey="ID"
              bordered
              size="small"
            />
          </Panel>
        </Collapse>
      
      </Form>

      {/* TextCrafter Modal */}
      {isTextCrafterVisible && (
        <TextCrafter
          initialText={craftedText}
          onSave={handleTextCrafterSave}
          onClose={() => setIsTextCrafterVisible(false)}
          dashboardStructure={dashboardStructure}
        />
      )}

      {/* ClauseEditor Modal */}
      {isClauseEditorVisible && (
        <ClauseEditor
          visible={isClauseEditorVisible}
          onClose={() => setIsClauseEditorVisible(false)}
          clause={editingClause}
          dashboardStructure={dashboardStructure}
          refreshClauses={handleNewClauseSaved}  // Use handleNewClauseSaved to add new clause
        
        />
      )}

      {/* AlertTestingResultsTable Modal for Clause Test Results*/}
      <AlertTestingResultsTable
        visible={isTestResultsModalVisible}
        onClose={() => setIsTestResultsModalVisible(false)}
        testResults={Array.isArray(currentTestResults) ? currentTestResults : []}
        // testResults={currentTestResults}
        columnNames={['Clause text', 'Ticker', 'User']}
      />
      {/* AlertTestingResultsTable Modal for Alert Test Results */}
      <AlertTestingResultsTable
    visible={isTestResultsModalVisible}
    onClose={() => setIsTestResultsModalVisible(false)}
    testResults={Array.isArray(currentTestResults) ? currentTestResults : []}
    columnNames={['Alert text', 'Ticker', 'User']} // Ensure data keys match expected structure in testResults
/>


    </Modal>
  );
};

export default AlertEditor;
